import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { Award, ArrowRight, CheckCircle2, FileSignature, Send, UserCheck, BadgeCheck, ClipboardList } from "lucide-react";
import { Bar, Doughnut, Radar } from "react-chartjs-2";
import {
    Chart as ChartJS,
    ArcElement,
    BarElement,
    CategoryScale,
    Legend,
    LinearScale,
    PointElement,
    LineElement,
    RadialLinearScale,
    Tooltip,
    Filler,
} from "chart.js";

import { Button } from "../ui/button";
import { cn } from "../../utils/tailwindUtils";

// Register every scale/element this component needs. Other pages that use
// chart.js do their own registration; chart.js dedupes, so calling here too
// is safe and keeps this component self-contained.
ChartJS.register(
    ArcElement,
    BarElement,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    RadialLinearScale,
    Tooltip,
    Legend,
    Filler,
);

// Same precedence the backend uses in compute_final_score: HR override beats
// RM rating, RM rating beats self rating. Treat 0 as a valid rating; only
// null/undefined falls through to the next layer.
const finalRatingFor = (g) => {
    if (g.hr_override_rating !== null && g.hr_override_rating !== undefined) return g.hr_override_rating;
    if (g.rm_rating !== null && g.rm_rating !== undefined) return g.rm_rating;
    if (g.self_rating !== null && g.self_rating !== undefined) return g.self_rating;
    return null;
};

const RATING_BAND = (score) => {
    if (score === null || score === undefined) return { label: "—", color: "text-gray-400" };
    const s = Number(score);
    if (s >= 4.5) return { label: "Outstanding", color: "text-emerald-600 dark:text-emerald-300" };
    if (s >= 3.5) return { label: "Exceeds Expectations", color: "text-sky-600 dark:text-sky-300" };
    if (s >= 2.5) return { label: "Meets Expectations", color: "text-amber-600 dark:text-amber-300" };
    if (s >= 1.5) return { label: "Needs Improvement", color: "text-orange-600 dark:text-orange-300" };
    return { label: "Poor", color: "text-red-600 dark:text-red-300" };
};

// Hero gauge — a single circular SVG ring that fills proportional to score/5.
// Built with raw SVG so it survives a chart.js failure and stays light-weight.
const ScoreRing = ({ score, label }) => {
    const numeric = score === null || score === undefined ? 0 : Number(score);
    const pct = Math.max(0, Math.min(1, numeric / 5));
    const size = 168;
    const stroke = 14;
    const radius = (size - stroke) / 2;
    const circumference = 2 * Math.PI * radius;
    const offset = circumference * (1 - pct);
    const band = RATING_BAND(score);

    return (
        <div className="relative inline-flex flex-col items-center justify-center">
            <svg width={size} height={size} className="-rotate-90">
                <circle
                    cx={size / 2}
                    cy={size / 2}
                    r={radius}
                    stroke="currentColor"
                    strokeWidth={stroke}
                    fill="none"
                    className="text-gray-200 dark:text-gray-700"
                />
                <circle
                    cx={size / 2}
                    cy={size / 2}
                    r={radius}
                    stroke="currentColor"
                    strokeWidth={stroke}
                    fill="none"
                    strokeDasharray={circumference}
                    strokeDashoffset={offset}
                    strokeLinecap="round"
                    className="text-sidebar-primary transition-[stroke-dashoffset] duration-700"
                />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-4xl font-semibold text-gray-900 dark:text-gray-100">
                    {numeric ? numeric.toFixed(1) : "—"}
                </span>
                <span className="text-xs text-muted-foreground">/ 5</span>
                <span className={cn("mt-1 text-xs font-medium", band.color)}>
                    {label || band.label}
                </span>
            </div>
        </div>
    );
};

// Horizontal workflow stepper showing each milestone with its date.
const TIMELINE_STEPS = [
    { key: "acknowledged_at", label: "Acknowledged", icon: ClipboardList },
    { key: "self_submitted_at", label: "Submitted", icon: Send },
    { key: "rm_reviewed_at", label: "RM Reviewed", icon: UserCheck },
    { key: "hr_approved_at", label: "HR Approved", icon: BadgeCheck },
    { key: "published_at", label: "Published", icon: Award },
];

const WorkflowTimeline = ({ appraisal }) => {
    return (
        <ol className="relative grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
            {TIMELINE_STEPS.map((step) => {
                const ts = appraisal[step.key];
                const done = !!ts;
                const Icon = step.icon;
                return (
                    <li
                        key={step.key}
                        className={cn(
                            "flex items-start gap-2 rounded-md border p-2.5",
                            done
                                ? "bg-emerald-50/60 dark:bg-emerald-900/15 border-emerald-200 dark:border-emerald-900/40"
                                : "bg-gray-50 dark:bg-gray-800/40 border-gray-200 dark:border-gray-700",
                        )}
                    >
                        <div
                            className={cn(
                                "rounded-full p-1.5 flex-shrink-0",
                                done
                                    ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300"
                                    : "bg-gray-200 text-gray-400 dark:bg-gray-700 dark:text-gray-500",
                            )}
                        >
                            <Icon className="w-3.5 h-3.5" />
                        </div>
                        <div className="min-w-0">
                            <p className="text-xs font-medium text-gray-900 dark:text-gray-100">{step.label}</p>
                            <p className="text-[10px] text-muted-foreground truncate">
                                {ts ? dayjs(ts).format("DD MMM YYYY") : "Pending"}
                            </p>
                        </div>
                    </li>
                );
            })}
        </ol>
    );
};

const EmployeePublishedSummary = ({ appraisal }) => {
    const navigate = useNavigate();
    const goals = appraisal?.goals || [];

    const labels = goals.map((g) => g.kpi_name);
    const selfData = goals.map((g) => g.self_rating ?? 0);
    const rmData = goals.map((g) => g.rm_rating ?? 0);
    const finalData = goals.map((g) => finalRatingFor(g) ?? 0);
    const weightages = goals.map((g) => g.weightage || 0);

    // Chart datasets — kept in useMemo so re-renders don't re-allocate. The
    // explicit colors avoid relying on chart.js defaults (which don't respect
    // dark mode).
    const barData = useMemo(() => ({
        labels,
        datasets: [
            {
                label: "Self",
                data: selfData,
                backgroundColor: "rgba(59,130,246,0.55)",
                borderColor: "rgba(59,130,246,1)",
                borderWidth: 1,
                borderRadius: 4,
            },
            {
                label: "Manager",
                data: rmData,
                backgroundColor: "rgba(139,92,246,0.55)",
                borderColor: "rgba(139,92,246,1)",
                borderWidth: 1,
                borderRadius: 4,
            },
            {
                label: "Final",
                data: finalData,
                backgroundColor: "rgba(16,185,129,0.65)",
                borderColor: "rgba(16,185,129,1)",
                borderWidth: 1,
                borderRadius: 4,
            },
        ],
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }), [goals]);

    const barOptions = useMemo(() => ({
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { position: "bottom", labels: { boxWidth: 12, font: { size: 11 } } },
            tooltip: { mode: "index", intersect: false },
        },
        scales: {
            y: {
                beginAtZero: true,
                max: 5,
                ticks: { stepSize: 1, font: { size: 11 } },
                grid: { color: "rgba(148,163,184,0.2)" },
            },
            x: {
                ticks: { font: { size: 11 }, maxRotation: 30, minRotation: 0 },
                grid: { display: false },
            },
        },
    }), []);

    const doughnutData = useMemo(() => ({
        labels,
        datasets: [{
            data: weightages,
            backgroundColor: [
                "rgba(59,130,246,0.75)",
                "rgba(139,92,246,0.75)",
                "rgba(16,185,129,0.75)",
                "rgba(245,158,11,0.75)",
                "rgba(244,63,94,0.75)",
                "rgba(14,165,233,0.75)",
                "rgba(168,85,247,0.75)",
            ],
            borderWidth: 1,
        }],
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }), [goals]);

    const doughnutOptions = useMemo(() => ({
        responsive: true,
        maintainAspectRatio: false,
        cutout: "60%",
        plugins: {
            legend: { position: "bottom", labels: { boxWidth: 10, font: { size: 11 } } },
            tooltip: {
                callbacks: {
                    label: (ctx) => ` ${ctx.label}: ${ctx.parsed}%`,
                },
            },
        },
    }), []);

    const radarData = useMemo(() => ({
        labels,
        datasets: [{
            label: "Final Rating",
            data: finalData,
            backgroundColor: "rgba(16,185,129,0.18)",
            borderColor: "rgba(16,185,129,1)",
            borderWidth: 2,
            pointBackgroundColor: "rgba(16,185,129,1)",
            pointRadius: 3,
        }],
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }), [goals]);

    const radarOptions = useMemo(() => ({
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: false } },
        scales: {
            r: {
                beginAtZero: true,
                suggestedMin: 0,
                suggestedMax: 5,
                ticks: { stepSize: 1, font: { size: 10 }, display: false },
                pointLabels: { font: { size: 10 } },
                grid: { color: "rgba(148,163,184,0.25)" },
                angleLines: { color: "rgba(148,163,184,0.25)" },
            },
        },
    }), []);

    if (!appraisal) return null;

    return (
        <div className="space-y-4">
            {/* Hero — score + cycle context + CTA */}
            <div className="rounded-lg border border-emerald-200 dark:border-emerald-900/40 bg-gradient-to-br from-emerald-50 to-white dark:from-emerald-900/15 dark:to-gray-900 p-4 sm:p-6">
                <div className="flex flex-col md:flex-row items-center md:items-stretch gap-6">
                    <div className="flex-shrink-0">
                        <ScoreRing score={appraisal.final_score} label={appraisal.final_rating} />
                    </div>
                    <div className="flex-1 min-w-0 flex flex-col justify-between text-center md:text-left">
                        <div>
                            <div className="inline-flex items-center gap-1.5 text-xs font-medium px-2 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 mb-2">
                                <Award className="w-3 h-3" />
                                Appraisal Published
                            </div>
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                                {appraisal.cycle_name || "Performance Review"}
                            </h3>
                            <p className="text-sm text-muted-foreground mt-1">
                                Reviewed by {appraisal.reporting_manager?.name || "your manager"}
                                {appraisal.published_at && (
                                    <> · Published on {dayjs(appraisal.published_at).format("DD MMM YYYY")}</>
                                )}
                            </p>
                        </div>
                        <div className="mt-4 flex flex-col sm:flex-row gap-2 justify-center md:justify-start">
                            <Button
                                size="sm"
                                variant="brand"
                                onClick={() => navigate(`/appraisal/my-appraisal/${appraisal.id}`)}
                            >
                                <FileSignature className="w-4 h-4 mr-1" /> View Full Appraisal
                                <ArrowRight className="w-4 h-4 ml-1" />
                            </Button>
                            <div className="flex items-center justify-center text-xs text-muted-foreground gap-3 px-1">
                                <span className="flex items-center gap-1">
                                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> Locked
                                </span>
                                <span>{goals.length} KPI{goals.length === 1 ? "" : "s"}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* KPI comparison bar (full width on mobile, 2/3 on desktop) + radar */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="lg:col-span-2 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 sm:p-5">
                    <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
                        <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Rating Comparison</h3>
                        <p className="text-xs text-muted-foreground">Self vs Manager vs Final (after HR review)</p>
                    </div>
                    <div className="h-72">
                        <Bar data={barData} options={barOptions} />
                    </div>
                </div>

                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 sm:p-5">
                    <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
                        <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">KPI Profile</h3>
                        <p className="text-xs text-muted-foreground">Final ratings</p>
                    </div>
                    <div className="h-72">
                        <Radar data={radarData} options={radarOptions} />
                    </div>
                </div>
            </div>

            {/* Weightage donut + per-KPI rating table */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 sm:p-5">
                    <div className="flex items-center justify-between mb-3">
                        <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Weightage</h3>
                    </div>
                    <div className="h-64">
                        <Doughnut data={doughnutData} options={doughnutOptions} />
                    </div>
                </div>

                <div className="lg:col-span-2 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 sm:p-5">
                    <h3 className="text-base font-semibold mb-3 text-gray-900 dark:text-gray-100">KPI Breakdown</h3>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="text-xs text-muted-foreground">
                                <tr className="text-left">
                                    <th className="py-2 pr-3 font-medium">KPI</th>
                                    <th className="py-2 px-2 font-medium text-center">Weight</th>
                                    <th className="py-2 px-2 font-medium text-center">Self</th>
                                    <th className="py-2 px-2 font-medium text-center">Manager</th>
                                    <th className="py-2 pl-2 font-medium text-center">Final</th>
                                </tr>
                            </thead>
                            <tbody>
                                {goals.map((g) => {
                                    const finalRating = finalRatingFor(g);
                                    return (
                                        <tr key={g.id} className="border-t border-gray-100 dark:border-gray-700">
                                            <td className="py-2 pr-3 font-medium text-gray-900 dark:text-gray-100">{g.kpi_name}</td>
                                            <td className="py-2 px-2 text-center text-muted-foreground">{g.weightage}%</td>
                                            <td className="py-2 px-2 text-center">{g.self_rating ?? "—"}</td>
                                            <td className="py-2 px-2 text-center">{g.rm_rating ?? "—"}</td>
                                            <td className="py-2 pl-2 text-center">
                                                <span className="inline-flex items-center justify-center min-w-[28px] px-2 py-0.5 rounded-md bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300 text-xs font-semibold">
                                                    {finalRating ?? "—"}
                                                </span>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* Workflow stepper */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 sm:p-5">
                <h3 className="text-base font-semibold mb-3 text-gray-900 dark:text-gray-100">Approval Journey</h3>
                <WorkflowTimeline appraisal={appraisal} />
            </div>
        </div>
    );
};

export default EmployeePublishedSummary;
